import { CheckBoxWithLabelWidget, TabStyles } from '@tableau/widgets';
import { Location } from 'history';
import * as queryString from 'query-string';
import React from 'react';

import { LoginUrlsWrapper } from '../app/spaces/Login/LoginUrls';
import { SignInLogo } from '../app/spaces/Login/SignInLogo';
import { Messages } from '../codegen/Localize';
import { ResultHelper } from '../httpClient/utils/ResultHelper';
import WebHttpClient from '../httpClient/WebHttpClient';
import {
  IPKCEGetAuthorizationCodeResult,
  PKCEGetAuthorizationCodeRequest
} from '../ts/ServerApi/requests/PKCEGetAuthorizationCodeRequest';
import { PKCEStore } from './PKCEStore';
import { PKCEUrls } from './PKCEUrls';

export interface GetAuthorizationCodeProps {
  appUriProtocol: string;
  codeChallenge: string;
  codeChallengeMethod: string;
  requireSignIn?: string;
  currentSessionIsValid: boolean;
  location: Location;
  targetSite: string;
}

const ProceedButtonStyle: React.CSSProperties = {
  marginBottom: TabStyles.Spacing.MediumSpace,
};

const CheckBoxContainerStyle: React.CSSProperties = {
  display: 'inline-block',
};

export const GetAuthorizationCode: React.FunctionComponent<GetAuthorizationCodeProps> = props => {
  const [ signingIn, setSigningIn ] = React.useState(false);
  const [ rememberMyChoice, setRememberMyChoice ] = React.useState(false);
  const [ checkedKnownProtocol, setCheckedKnownProtocol ] = React.useState(false);

  React.useEffect(
    () => {
      let didCancel = false;

      const requireSignIn = parseBooleanFrom(props.requireSignIn);
      if (requireSignIn === true || !props.currentSessionIsValid) {
        // Make sure there isn't any requireSignIn param post sign in
        const redirectUrl = PKCEUrls.getAuthorizationCodePath() + `?${queryString.stringify({
          appUriProtocol: props.appUriProtocol,
          codeChallenge: props.codeChallenge,
          codeChallengeMethod: props.codeChallengeMethod
        })}`;

        LoginUrlsWrapper.navigateToSignIn({
          ':deepLinkingDisabled': true, // Disabling as deeplinking logic can lead to custom url scheme tableau:// that prematurely ends the pkce flow
          redirect: redirectUrl,
          site: props.targetSite,
          disableAppBanner: true
        });
      } else {
        PKCEStore.isAppUriProtocolKnown(props.appUriProtocol)
          .then(known => {
            if (didCancel) return;
            if (known) {
              setSigningIn(true);
              supplyAuthorizationCodeToApp(props.appUriProtocol, props.codeChallenge, props.codeChallengeMethod);
            }
            setCheckedKnownProtocol(true);
          });
      }

      return () => didCancel = true;
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ props.requireSignIn, props.currentSessionIsValid ]
  );

  const handleProceed = () => {
    setSigningIn(true);
    handleAppUriProtocol(props.appUriProtocol, rememberMyChoice)
      .then(() => supplyAuthorizationCodeToApp(props.appUriProtocol, props.codeChallenge, props.codeChallengeMethod));
  };

  if (!checkedKnownProtocol) {
    return null;
  }

  if (signingIn) {
    return (
      <GetAuthorizationCodeContainer>
        <div className="tb-login-title">{Messages.pkceYouMayCloseThisWindow()}</div>
        <span className="tb-icon-spinner-large-alternate"/>
      </GetAuthorizationCodeContainer>
    );
  }

  return (
    <GetAuthorizationCodeContainer>
      <div className="tb-login-title">{Messages.pkceConfirmAppUriProtocol()}</div>
      <div className="tb-login-title">{props.appUriProtocol}</div>
      <button className="tb-orange-button" style={ProceedButtonStyle} onClick={handleProceed}>
        <span>{Messages.proceed()}</span>
      </button>
      <div>
        <CheckBoxWithLabelWidget
          checked={rememberMyChoice}
          handleChange={() => setRememberMyChoice(!rememberMyChoice)}
          containerStyle={CheckBoxContainerStyle}
          label={Messages.rememberMyChoice()}
          removeLeftMargin={true}
          testId="remember-my-choice"
        />
      </div>
    </GetAuthorizationCodeContainer>
  );
};

export const GetAuthorizationCodeContainer: React.FunctionComponent = props => {
  return (
    <div className="tb-login">
      <span className="tb-login-form-container">
        <SignInLogo/>
        <div>{props.children}</div>
      </span>
    </div>
  );
};

function handleAppUriProtocol(appUriProtocol: string, rememberMyChoice: boolean) {
  return rememberMyChoice ? PKCEStore.addKnownAppUriProtocol(appUriProtocol) : Promise.resolve();
}

function supplyAuthorizationCodeToApp(
  appUriProtocol: string,
  codeChallenge: string,
  codeChallengeMethod: string
): Promise<any> {
  const request = new PKCEGetAuthorizationCodeRequest({
    codeChallenge: codeChallenge,
    codeChallengeMethod: codeChallengeMethod,
  });
  return WebHttpClient.sendRequest(request)
    .then(ResultHelper.rejectResultOnError())
    .then((result: IPKCEGetAuthorizationCodeResult) => {
      window.location.href = PKCEUrls.appLoginUrl(appUriProtocol, result.authorizationCode, window.location.host);
    });
}

function parseBooleanFrom(stringParam: string) {
  if (typeof stringParam === 'undefined') {
    // parameter not specified
    return false;
  } else if (stringParam === null) {
    // ?requireSignIn
    return true;
  } else {
    // ?requireSignIn=true|false)
    return stringParam === 'true';
  }
}
